import { createSelector } from 'reselect';
import { List, Map, OrderedMap, Set } from 'immutable';
import get from 'lodash/get';

import Search from 'models/search';
import { Locations } from 'models/locations';
import { Events } from 'models/event';
import { Sellers } from 'models/seller';
import Coordinates from 'models/coordinates';
import { StreetParkingSegments } from 'models/street-parking-segment';

const readProp = (slice, key, defaultValue) => {
  if (!slice) {
    return defaultValue;
  }
  if (typeof slice.get === 'function') {
    const val = slice.get(key);
    return val !== undefined ? val : defaultValue;
  }
  return get(slice, key, defaultValue);
};

export const getSearchState = state => get(state, 'search', null);

export const getCurrentSearch = createSelector(
  [getSearchState],
  search => readProp(search, 'currentSearch', new Search()),
);

export const getPreviousSearch = createSelector(
  [getSearchState],
  search => readProp(search, 'previousSearch', new Search()),
);

export const getLocations = createSelector(
  [getSearchState],
  search => readProp(search, 'locations', Locations()),
);

export const getSelectedLocation = createSelector(
  [getSearchState],
  search => readProp(search, 'selectedLocation', null),
);

export const getSelectedQuote = createSelector(
  [getSearchState],
  search => readProp(search, 'selectedQuote', null),
);

export const getHighlightedLocation = createSelector(
  [getSearchState],
  search => readProp(search, 'highlightedLocation', null),
);

export const getScrolledLocation = createSelector(
  [getSearchState],
  search => readProp(search, 'scrolledLocation', null),
);

export const getVenue = createSelector(
  [getSearchState],
  search => readProp(search, 'venue', null),
);

export const getEvent = createSelector(
  [getSearchState],
  search => readProp(search, 'event', null),
);

export const getEventName = createSelector(
  [getSearchState],
  search => readProp(search, 'eventName', null),
);

export const getEvents = createSelector(
  [getSearchState],
  search => readProp(search, 'events', Events()),
);

export const getHub = createSelector(
  [getSearchState],
  search => readProp(search, 'hub', null),
);

export const getNearbyMetros = createSelector(
  [getSearchState],
  search => readProp(search, 'nearbyMetros', List()),
);

export const getDisplayMap = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'displayMap', false)),
);

export const getMonthlyAvailable = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'monthlyAvailable', false)),
);

export const getSearchSellers = createSelector(
  [getSearchState],
  search => readProp(search, 'sellers', Sellers()),
);

export const getGeoIPLocation = createSelector(
  [getSearchState],
  search => readProp(search, 'geoIPLocation', new Coordinates()),
);

export const getTimePickerPromptDismissed = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'timePickerPromptDismissed', false)),
);

export const getCurationDismissed = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'curationDismissed', false)),
);

export const getStreetParking = createSelector(
  [getSearchState],
  search => readProp(search, 'streetParking', StreetParkingSegments()),
);

export const getShowStreetParking = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'showStreetParking', false)),
);

export const getSearchInitialized = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'initialized', false)),
);

export const getVenueInitialized = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'venueInitialized', false)),
);

export const getHubInitialized = createSelector(
  [getSearchState],
  search => Boolean(readProp(search, 'hubInitialized', false)),
);

export const getRestrictionsModalDismissals = createSelector(
  [getSearchState],
  search => readProp(search, 'restrictionsModalDismissals', Set()),
);

export const getSearchRequiredFields = createSelector(
  [getSearchState],
  search => readProp(search, 'requiredFields', Set()),
);

export const getSearchRequiredFieldErrors = createSelector(
  [getSearchState],
  search => readProp(search, 'requiredFieldErrors', OrderedMap()),
);

export const getSearchError = createSelector(
  [getSearchState],
  search => readProp(search, 'error', Map()),
);
